/**
 * A tela de Bater Ponto do painel admin, aberta pelo relógio de pé do escritório.
 *
 * É uma cópia de `apps/admin/src/pages/BaterPonto.tsx` (project-green): os
 * mesmos contadores, os mesmos botões, a mesma lista e os mesmos dois diálogos,
 * com os textos de lá e as contas de lá (`lib/ponto.ts`). O ponto não vive aqui —
 * cada leitura e cada batida vão ao painel, pela API do GreenVoice, e ficam na
 * mesma tabela onde a tela do painel as lê. Bater aqui ou lá é o mesmo registo.
 *
 * O que muda em relação ao painel é só o que tinha de mudar:
 *  - a hora da batida é a do servidor (o navegador só manda o fuso);
 *  - a ordem das batidas conta a partir de HOJE, não do dia escolhido no
 *    calendário — no painel, com outro dia seleccionado, a sequência lia-se
 *    desse dia.
 */
import { type PontoRegistro, type PontoTipo, podeBaterPonto, ultimoTipo } from "@greenvoice/shared";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { ApiError, api } from "../lib/api";
import {
  daSemana,
  dataDoRegisto,
  dataLocal,
  diaEMes,
  doDia,
  horaDoRegisto,
  horas,
  resumoDoMes,
  tempoDoDia,
} from "../lib/ponto";

import { IconClose } from "./Icons";

type NomeIcone = "relogio" | "entrar" | "sair" | "talheres" | "cafe" | "grafico";

/** Os ícones da tela do painel (lucide), desenhados aqui para não trazer a biblioteca. */
const TRACOS: Record<NomeIcone, ReactNode> = {
  relogio: (
    <>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </>
  ),
  entrar: (
    <>
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <polyline points="10 17 15 12 10 7" />
      <line x1="15" x2="3" y1="12" y2="12" />
    </>
  ),
  sair: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" x2="9" y1="12" y2="12" />
    </>
  ),
  talheres: (
    <>
      <path d="m16 2-2.3 2.3a3 3 0 0 0 0 4.2l1.8 1.8a3 3 0 0 0 4.2 0L22 8" />
      <path d="M15 15 3.3 3.3a4.2 4.2 0 0 0 0 6l7.3 7.3c.7.7 2 .7 2.8 0L15 15Zm0 0 7 7" />
      <path d="m2.1 21.8 6.4-6.3" />
      <path d="m19 5-7 7" />
    </>
  ),
  cafe: (
    <>
      <path d="M17 8h1a4 4 0 1 1 0 8h-1" />
      <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z" />
      <line x1="6" x2="6" y1="2" y2="4" />
      <line x1="10" x2="10" y1="2" y2="4" />
      <line x1="14" x2="14" y1="2" y2="4" />
    </>
  ),
  grafico: (
    <>
      <path d="M3 3v18h18" />
      <path d="M18 17V9" />
      <path d="M13 17V5" />
      <path d="M8 17v-3" />
    </>
  ),
};

function Icone({ nome, size = 20 }: { nome: NomeIcone; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {TRACOS[nome]}
    </svg>
  );
}

/** Os quatro cartões do diálogo, pela ordem da tela do painel. */
const CARTOES: readonly { tipo: PontoTipo; tom: string; icone: NomeIcone }[] = [
  { tipo: "Entrada", tom: "entrada", icone: "entrar" },
  { tipo: "Saída", tom: "saida", icone: "sair" },
  { tipo: "Início almoço", tom: "almoco", icone: "talheres" },
  { tipo: "Retorno almoço", tom: "retorno", icone: "cafe" },
];

const TOM_DO_TIPO: Record<string, string> = {
  Entrada: "entrada",
  Saída: "saida",
  "Início almoço": "almoco",
  "Retorno almoço": "retorno",
};

const maisRecentePrimeiro = (a: PontoRegistro, b: PontoRegistro) =>
  new Date(b.data_hora).getTime() - new Date(a.data_hora).getTime() || b.id - a.id;

function mensagemDe(erro: unknown): string {
  if (erro instanceof ApiError && erro.message) return erro.message;
  return "Não foi possível falar com o ponto. Tenta outra vez.";
}

export function PontoModal({ onClose }: { onClose: () => void }) {
  const [registros, setRegistros] = useState<PontoRegistro[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [dataSel, setDataSel] = useState(() => dataLocal());
  const [mes, setMes] = useState(() => dataLocal().slice(0, 7));
  const [escolher, setEscolher] = useState(false);
  const [resumoAberto, setResumoAberto] = useState(false);
  // Os contadores andam ao segundo, como na tela do painel.
  const [agora, setAgora] = useState(() => new Date());

  useEffect(() => {
    let vivo = true;
    api.ponto
      .meus()
      .then((r) => {
        if (vivo) setRegistros(r.registros);
      })
      .catch((e: unknown) => {
        if (vivo) setErro(mensagemDe(e));
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    const t = window.setInterval(() => setAgora(new Date()), 1000);
    return () => window.clearInterval(t);
  }, []);

  // Escape fecha o que estiver por cima: primeiro um diálogo, depois a tela.
  const fecharTopo = useCallback(() => {
    if (escolher) setEscolher(false);
    else if (resumoAberto) setResumoAberto(false);
    else onClose();
  }, [escolher, resumoAberto, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") fecharTopo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fecharTopo]);

  const hoje = dataLocal(agora);
  const eHoje = dataSel === hoje;
  const doDiaEscolhido = useMemo(() => doDia(registros, dataSel), [registros, dataSel]);
  const tempo = tempoDoDia(doDiaEscolhido, agora);
  const semana = horas(daSemana(registros, dataSel), agora);
  const ultimoDeHoje = useMemo(() => ultimoTipo(doDia(registros, hoje)), [registros, hoje]);
  const semanas = useMemo(
    () => (resumoAberto ? resumoDoMes(registros, mes, agora) : []),
    [resumoAberto, registros, mes, agora],
  );

  const bater = async (tipo: PontoTipo) => {
    setGravando(true);
    setErro(null);
    try {
      const r = await api.ponto.bater(tipo, Intl.DateTimeFormat().resolvedOptions().timeZone);
      setRegistros(r.registros);
      setDataSel(dataLocal());
      setEscolher(false);
    } catch (e) {
      setErro(mensagemDe(e));
      setEscolher(false);
    } finally {
      setGravando(false);
    }
  };

  return createPortal(
    <div
      className="modal ponto"
      role="dialog"
      aria-modal="true"
      aria-label="Bater Ponto"
      // O jogo ouve o teclado na `window` (WASD, E, T): sem isto, escrever no
      // calendário também andava com o boneco. O Escape é tratado aqui porque
      // já não chega à `window`.
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Escape") fecharTopo();
      }}
    >
      <div className="ponto__janela">
        <button type="button" className="ponto__fechar" onClick={onClose} title="Fechar" aria-label="Fechar">
          <IconClose size={18} />
        </button>

        <section className="ponto__cartao">
          <header className="ponto__cab">
            <span className="ponto__selo">
              <Icone nome="relogio" size={24} />
            </span>
            <div>
              <h2 className="ponto__titulo">Bater Ponto</h2>
              <p className="ponto__sub">Registre sua entrada, saída e horário de almoço</p>
            </div>
          </header>

          {tempo.texto || semana ? (
            <div className="ponto__contadores">
              {tempo.texto ? (
                <div className={`ponto__contador ponto__contador--dia${tempo.aTrabalhar ? " is-on" : ""}`}>
                  <div>
                    <p className="ponto__contador-nome">{eHoje ? "Tempo de trabalho hoje" : "Tempo trabalhado"}</p>
                    <p className="ponto__contador-valor">{tempo.texto}</p>
                  </div>
                  <Icone nome="relogio" size={48} />
                </div>
              ) : null}
              {semana ? (
                <div className={`ponto__contador ponto__contador--semana${tempo.aTrabalhar ? " is-on" : ""}`}>
                  <div>
                    <p className="ponto__contador-nome">Horas trabalhadas esta semana</p>
                    <p className="ponto__contador-valor">{semana}</p>
                  </div>
                  <Icone nome="relogio" size={48} />
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="ponto__accoes">
            <button
              type="button"
              className="ponto__botao ponto__botao--principal"
              onClick={() => setEscolher(true)}
              disabled={gravando || carregando}
            >
              <Icone nome="relogio" size={16} /> Bater Ponto
            </button>
            <input
              type="date"
              className="ponto__data"
              aria-label="Dia"
              value={dataSel}
              onChange={(e) => setDataSel(e.target.value || dataLocal())}
            />
            <button type="button" className="ponto__botao ponto__botao--hoje" onClick={() => setDataSel(dataLocal())}>
              Hoje
            </button>
            <button type="button" className="ponto__botao ponto__botao--contorno" onClick={() => setResumoAberto(true)}>
              <Icone nome="grafico" size={16} /> Resumo Mensal
            </button>
          </div>

          {erro ? (
            <p className="ponto__erro" role="alert">
              {erro}
            </p>
          ) : null}
        </section>

        <section className="ponto__cartao ponto__cartao--lista">
          <div className="ponto__lista-cab">
            <h3 className="ponto__lista-titulo">
              {eHoje ? "Registros de Hoje" : `Registros de ${dataSel.split("-").reverse().join("/")}`}
            </h3>
            {!eHoje ? (
              <button type="button" className="ponto__voltar" onClick={() => setDataSel(dataLocal())}>
                Voltar para hoje
              </button>
            ) : null}
          </div>
          {carregando ? (
            <div className="ponto__vazio">Carregando...</div>
          ) : doDiaEscolhido.length === 0 ? (
            <div className="ponto__vazio">Nenhum registro nesta data</div>
          ) : (
            <ul className="ponto__registos">
              {[...doDiaEscolhido].sort(maisRecentePrimeiro).map((r) => (
                <li key={r.id} className="ponto__registo">
                  <div className="ponto__registo-esq">
                    <span className={`ponto__selo-tipo ponto__selo-tipo--${TOM_DO_TIPO[r.tipo] ?? "outro"}`}>
                      {r.tipo}
                    </span>
                    <div>
                      <p className="ponto__registo-hora">{horaDoRegisto(r.data_hora)}</p>
                      <p className="ponto__registo-data">{dataDoRegisto(r.data_hora)}</p>
                    </div>
                  </div>
                  {r.timezone_name ? <span className="ponto__fuso">{r.timezone_name}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {escolher ? (
        <div className="ponto__dialogo" role="dialog" aria-modal="true" aria-label="Selecione o tipo de registro">
          <div className="ponto__dialogo-janela">
            <div className="ponto__dialogo-cab">
              <h3>Selecione o tipo de registro</h3>
              <button type="button" className="ponto__x" onClick={() => setEscolher(false)} aria-label="Fechar">
                <IconClose size={20} />
              </button>
            </div>
            <div className="ponto__tipos">
              {CARTOES.map(({ tipo, tom, icone }) => (
                <button
                  key={tipo}
                  type="button"
                  className={`ponto__tipo ponto__tipo--${tom}`}
                  onClick={() => void bater(tipo)}
                  disabled={gravando || !podeBaterPonto(ultimoDeHoje, tipo)}
                >
                  <span className="ponto__tipo-icone">
                    <Icone nome={icone} size={32} />
                  </span>
                  <span className="ponto__tipo-nome">{tipo}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {resumoAberto ? (
        <div className="ponto__dialogo" role="dialog" aria-modal="true" aria-label="Resumo Mensal">
          <div className="ponto__dialogo-janela ponto__dialogo-janela--largo">
            <div className="ponto__dialogo-cab">
              <h3>Resumo Mensal</h3>
              <button type="button" className="ponto__x" onClick={() => setResumoAberto(false)} aria-label="Fechar">
                <IconClose size={20} />
              </button>
            </div>
            <div className="ponto__dialogo-corpo">
              <label className="ponto__mes-rotulo">
                Selecione o mês
                <input
                  type="month"
                  className="ponto__mes"
                  value={mes}
                  onChange={(e) => setMes(e.target.value || mes)}
                />
              </label>
              {semanas.length === 0 ? (
                <div className="ponto__vazio">Nenhum registro para o mês selecionado</div>
              ) : (
                <div className="ponto__semanas">
                  {semanas.map((s, i) => {
                    const viva = s.semanaActual && tempo.aTrabalhar;
                    return (
                      <div key={s.inicio} className={`ponto__semana${viva ? " is-on" : ""}`}>
                        <div className="ponto__semana-cab">
                          <span>Semana {i + 1}</span>
                          {viva ? <span className="ponto__pulso" aria-hidden="true" /> : null}
                        </div>
                        <p className="ponto__semana-datas">
                          {diaEMes(s.inicio)} - {diaEMes(s.fim)}
                        </p>
                        <p className="ponto__semana-horas">{viva ? semana : s.horas}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
